import { apiClient } from "../../../shared/api/client";
import type { operations } from "../../../shared/api/generated";

// Gestion des DEVICES (tokens push FCM/APNs) — openapi v2.
//
// Le backend route les notifications push vers les devices enregistrés d'un
// utilisateur. Le client doit donc :
//   • POST   /devices            à la connexion (enregistre le token natif)
//   • DELETE /devices            à la déconnexion (retire le token)
//   • GET    /devices/{userId}   (debug / écran "appareils connectés")
//
// Le token est un token FCM (Android et web) ou APNs (iOS),
// pas l'Expo push token — c'est ce que le service Notification backend attend.

export type DevicePlatform = NonNullable<
  operations["registerDevice"]["requestBody"]["content"]["application/json"]["platform"]
>;

// Réponse GET /devices/{userId} — dérivée du contrat (champs optionnels côté
// backend ; tokens tronqués pour sécurité). `data` est déballé par l'intercepteur.
export type DeviceList = NonNullable<
  operations["listDevices"]["responses"]["200"]["content"]["application/json"]["data"]
>;
export type RegisteredDevice = NonNullable<DeviceList["devices"]>[number];

// Enregistre (ou met à jour) un token device pour l'utilisateur. Idempotent
// côté serveur : ré-enregistrer le même token ne crée pas de doublon.
export async function registerDevice(params: {
  userId:   string;
  token:    string;
  platform: DevicePlatform;
}): Promise<void> {
  await apiClient.post("/devices", {
    user_id:  params.userId,
    token:    params.token,
    platform: params.platform,
  });
}

// Retire un token device (déconnexion / désinstallation logique). `userId` est
// optionnel côté contrat mais on le fournit toujours quand on l'a.
//
// `authToken` permet de forcer le Bearer explicitement : au logout, la session
// est purgée juste après l'appel, donc l'intercepteur ne trouverait plus le
// token au moment de l'envoi → 401. On le passe ici pour garantir l'auth.
export async function unregisterDevice(params: {
  userId?:    string;
  token:      string;
  authToken?: string;
}): Promise<void> {
  await apiClient.delete("/devices", {
    data: {
      ...(params.userId ? { user_id: params.userId } : {}),
      token: params.token,
    },
    ...(params.authToken
      ? { headers: { Authorization: `Bearer ${params.authToken}` } }
      : {}),
  });
}

// Liste les devices enregistrés d'un utilisateur (debug / gestion appareils).
export async function listDevices(userId: string): Promise<DeviceList> {
  const { data } = await apiClient.get<DeviceList>(
    `/devices/${encodeURIComponent(userId)}`,
  );
  return data;
}
